<template>
  <div class="message-notification-container">
    <!-- 通知弹窗 -->
    <transition-group name="notification" tag="div" class="notification-wrapper">
      <div
        v-if="currentNotification"
        :key="currentNotification.id"
        class="notification-item"
        :class="['notification-success']"
      >
        <!-- 通知头部 -->
        <div class="notification-header">
          <div class="notification-icon">
            <i class="el-icon-success"></i>
          </div>
          <div class="notification-title">你有一条新的检测数据</div>
          <div class="notification-close" @click="handleCloseClick">
            <i class="el-icon-close"></i>
          </div>
        </div>
        
        <!-- 通知内容 -->
        <div class="notification-content">
          <p class="notification-time">
            通知时间：{{ formatTime(currentNotification.timestamp) }}
          </p>
          <div class="notification-countdown">
            {{ countdownText }}
          </div>
        </div>
        
        <!-- 通知操作 -->
        <div class="notification-actions">
          <button class="notification-btn" @click="handleViewClick">
            查看
          </button>
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script>
// 导出类和实例
import crossTabSync, { CrossTabSync } from '@/utils/crossTabSync';

import webSocketManager, { WebSocketManager } from "@/utils/webSocketLink.js";

export default {
  name: 'MessageNotification',
  
  data() {
    return {
      currentNotification: null, // 当前显示的通知对象 { id, timestamp }
      countdownTimer: null, // 倒计时定时器，每秒更新剩余时间
      remainingSeconds: 30, // 剩余倒计时秒数，用于显示"X秒后自动关闭"
    }
  },

  computed: {
    countdownText() {
      return `${this.remainingSeconds}秒后自动关闭`
    }
  },

  mounted() {
    this.initEventListeners()
  },

  beforeDestroy() {
    this.cleanup()
  },

  methods: {
    /**
     * 初始化事件监听器
     */
    initEventListeners() {
      // 监听WebSocket远程推送通知事件
      window.addEventListener(WebSocketManager.DIY_EVENT_NAME, this.handleRemotePushNotification)
      // 监听跨标签页本地推送通知事件
      window.addEventListener(CrossTabSync.DIY_EVENT_NAME, this.handleLocalPushNotification)
    },
    /**
     * 处理远程推送通知（来自WebSocket）
     */
    handleRemotePushNotification(event) {
      const { id, timestamp } = event.detail
      
      // 如果广播的消息id与当前通知的id相同，那么结束处理
      if (this.currentNotification && id === this.currentNotification.id) {
        return
      }
      
      this.showNotification(id, timestamp)
    },
    /**
     * 处理本地推送通知（来自跨标签页同步）
     */
    handleLocalPushNotification(event) {
      this.closeCurrentNotification();
    },
    /**
     * 显示通知
     */
    showNotification(id, timestamp) {
      // 关闭当前通知（如果有）
      this.closeCurrentNotification()
      
      // 显示新通知
      this.currentNotification = { id, timestamp }
      
      // 启动倒计时定时器
      this.startCountdownTimer()
    },

    /**
     * 启动倒计时定时器
     */
    startCountdownTimer() {
      // 清除之前的定时器
      this.clearTimers()
      
      // 重置倒计时
      this.remainingSeconds = 30
      
      // 每秒更新倒计时显示
      this.countdownTimer = setInterval(() => {
        this.remainingSeconds--
        
        if (this.remainingSeconds <= 0) {
          this.closeCurrentNotification()
        }
      }, 1000)
    },

    /**
     * 清除定时器
     */
    clearTimers() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
    },

    /**
     * 关闭当前通知
     */
    closeCurrentNotification() {
      if (this.currentNotification) {
        // 清除定时器
        this.clearTimers()
        
        // 清空当前通知状态
        this.currentNotification = null
      }
    },

    /**
     * 处理用户点击查看按钮
     */
    handleViewClick() {
      crossTabSync.broadcastConsumeNotification(this.currentNotification.id, "view");
      // 关闭当前通知
      this.closeCurrentNotification()
      
      // 跳转到消息详情页面，如果已在目标页面则刷新当前页面
      const targetPath = "/dataFolder/word"
      if (this.$route.path !== targetPath) {
        this.$router.push(targetPath)
      } else {
        this.$router.go(0)
      }
    },

    /**
     * 处理用户点击关闭按钮
     */
    handleCloseClick() {
      crossTabSync.broadcastConsumeNotification(this.currentNotification.id, "close");
      // 关闭当前通知
      this.closeCurrentNotification()
    },

    /**
     * 格式化时间
     */
    formatTime(timestamp) {
      const date = new Date(timestamp)
      return date.toLocaleTimeString('zh-CN', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    },

    /**
     * 清理资源
     */
    cleanup() {
      // 移除事件监听器
      window.removeEventListener(WebSocketManager.DIY_EVENT_NAME, this.handleRemotePushNotification)
      window.removeEventListener(CrossTabSync.DIY_EVENT_NAME, this.handleLocalPushNotification)
    }
  }
}
</script>

<style scoped>
.message-notification-container {
  position: relative;
}

.notification-wrapper {
  position: fixed;
  bottom: 0;
  right: 0;
  z-index: 2000;
  pointer-events: none;
}

.notification-item {
  position: relative;
  width: 330px;
  margin: 0 16px 16px 0;
  padding: 14px 26px 14px 13px;
  background-color: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  pointer-events: auto;
  overflow: hidden;
}

.notification-success {
  border-left: 4px solid #67c23a;
}

.notification-header {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
}

.notification-icon {
  width: 24px;
  height: 24px;
  margin-right: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification-icon i {
  font-size: 24px;
  color: #67c23a;
}

.notification-title {
  flex: 1;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  line-height: 24px;
}

.notification-close {
  position: absolute;
  top: 18px;
  right: 15px;
  cursor: pointer;
  color: #909399;
  font-size: 16px;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification-close:hover {
  color: #606266;
}

.notification-content {
  margin-left: 37px;
  margin-bottom: 12px;
}

.notification-time {
  margin: 0 0 8px 0;
  font-size: 14px;
  line-height: 1.4;
  color: #606266;
}

.notification-countdown {
  margin-bottom: 12px;
  font-size: 12px;
  color: #f56c6c;
}

.notification-actions {
  margin-left: 37px;
  text-align: right;
}

.notification-btn {
  background: #409EFF;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: background-color 0.3s;
}

.notification-btn:hover {
  background: #66b1ff;
}

.notification-btn:active {
  background: #3a8ee6;
}

/* 动画效果 */
.notification-enter-active {
  transition: all 0.3s ease;
}

.notification-leave-active {
  transition: all 0.3s ease;
}

.notification-enter {
  transform: translateY(100%);
  opacity: 0;
}

.notification-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.notification-move {
  transition: transform 0.3s ease;
}
</style>


// WEBPACK FOOTER //
// src/components/MessageNotification.vue